import { useNavigate, Link } from 'react-router-dom';
import { useEffect } from 'react';
import {
  BatteryCharging, Gauge, Disc3, Droplets, Activity, CarFront, Lightbulb,
  Wrench, FileText, Trophy, ShieldCheck, MapPin, FileCheck2, UserCheck,
  Phone, ArrowRight, ChevronRight, HelpCircle, CheckCircle2, Sun,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Seo } from '../components/academy/Seo';
import { ORGANIZATION_SCHEMA } from '../data/seoSchemas';

const HERO_IMG =
  'https://static.prod-images.emergentagent.com/jobs/a458f44e-02cd-4e55-83d8-3e9684068794/images/4fd4f07c34f42fd1c8d4824c96f1a89188b9eb33397b8f76cf35945d93dcd8e4.jpeg';

const WHY_MATTERS = [
  { Icon: Wrench, title: 'Avoid Costly Breakdowns', desc: 'Catch small problems before they become expensive repairs on the side of the highway.' },
  { Icon: ShieldCheck, title: 'Travel With Confidence', desc: 'Know your vehicle is truly prepared for long-distance, high-heat travel.' },
  { Icon: UserCheck, title: 'Protect Your Family', desc: 'Reduce the chance of an unexpected roadside emergency far from home.' },
  { Icon: FileCheck2, title: 'Peace of Mind', desc: 'Receive a professional digital inspection report before you ever leave the driveway.' },
];

const INCLUDED = [
  { Icon: BatteryCharging, label: 'Battery Health Test' },
  { Icon: Gauge, label: 'Tire Safety & Tread Inspection' },
  { Icon: Disc3, label: 'Brake Inspection' },
  { Icon: Droplets, label: 'Fluid Level Inspection' },
  { Icon: Activity, label: 'OBD-II Diagnostic Scan' },
  { Icon: CarFront, label: 'Suspension Visual Inspection' },
  { Icon: Lightbulb, label: 'Lights & Wiper Inspection' },
  { Icon: Wrench, label: 'Belt & Hose Inspection' },
  { Icon: FileText, label: 'Digital Vehicle Health Report' },
  { Icon: Trophy, label: 'Road Trip Readiness Score' },
];

const SCORE_LEVELS = [
  { label: 'Excellent', color: '#10b981' },
  { label: 'Very Good', color: '#84cc16' },
  { label: 'Good', color: '#eab308' },
  { label: 'Needs Attention', color: '#f97316' },
  { label: 'Not Recommended Until Repaired', color: '#ef4444' },
];

const WHY_CARLENS = [
  { Icon: ShieldCheck, title: 'Independent Vehicle Inspection', desc: 'We work for you — not a repair shop trying to sell you services.' },
  { Icon: MapPin, title: 'Mobile Service', desc: 'We come to your home, office, or wherever your vehicle is located.' },
  { Icon: FileText, title: 'Professional Digital Reports', desc: 'Easy-to-read inspection reports with photos and clear recommendations.' },
  { Icon: UserCheck, title: 'Experienced Inspectors', desc: 'Professional inspections designed to help prevent unexpected breakdowns.' },
];

const PERFECT_FOR = [
  'Families going on vacation', 'Disney trips', 'Beach vacations', 'College students',
  'Business travelers', 'Holiday travel', 'Cross-country road trips', 'Towing trailers',
  'New drivers', 'Military PCS moves',
];

const FAQS = [
  { q: 'What is included in the inspection?', a: 'The Road Trip Ready Inspection covers a battery health test, tire safety and tread inspection, brake inspection, fluid level check, OBD-II diagnostic scan, suspension visual inspection, lights and wiper inspection, and a belt and hose inspection. You also receive a digital Vehicle Health Report with photos and a personalized Road Trip Readiness Score.' },
  { q: 'Does this include repairs?', a: 'No. CarLens provides an independent inspection and recommendations but does not perform repairs during the inspection. You are free to take our report to any mechanic you trust.' },
  { q: 'How long does the inspection take?', a: 'Approximately 45–60 minutes, depending on the vehicle.' },
  { q: 'Do you come to me?', a: 'Yes. CarLens is a mobile inspection service — we come to your home, office, or wherever your vehicle is located throughout Northeast Florida.' },
  { q: 'Will I receive a report?', a: 'Yes. Every customer receives a professional digital Vehicle Health Report and a Road Trip Readiness Score.' },
];

const RoadTripReady = () => {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const path = '/road-trip-ready';
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Road Trip Ready Inspection', path },
  ];

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Road Trip Ready Vehicle Inspection',
    name: 'CarLens Road Trip Ready Inspection',
    description:
      'Mobile pre-road-trip vehicle safety inspection including battery testing, brakes, tires, fluids, suspension, diagnostics and more, with a digital Vehicle Health Report and Road Trip Readiness Score.',
    provider: { '@type': 'AutomotiveBusiness', name: 'CarLens Inspections', telephone: '+13213003256', email: 'carlensauto@gmail.com', slogan: 'Know the Truth Before You Buy.' },
    areaServed: { '@type': 'AdministrativeArea', name: 'Northeast Florida' },
    offers: { '@type': 'Offer', price: '99', priceCurrency: 'USD', availability: 'https://schema.org/InStock' },
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0e0e10 0%, #17171a 55%, #0e0e10 100%)' }}>
      <Seo
        siteName="CarLens"
        title="Road Trip Vehicle Inspection | Road Trip Ready Inspection | NE Florida"
        description="Travel with confidence. CarLens Road Trip Ready Inspection includes battery testing, brake inspection, tire safety check, diagnostic scan, fluids, suspension inspection and more for only $99. Mobile service throughout Northeast Florida."
        path={path}
        image={HERO_IMG}
        breadcrumbs={breadcrumbs}
        faq={FAQS}
        extraSchemas={[ORGANIZATION_SCHEMA, serviceSchema]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="rtr-hero">
        <img src={HERO_IMG} alt="Black luxury family SUV overlooking a scenic coastal highway at sunrise, ready for a road trip" className="absolute inset-0 w-full h-full object-cover" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-[#0e0e10]" />
        <div className="absolute -top-24 left-1/3 w-[520px] h-[300px] bg-orange-500/25 blur-[140px] rounded-full pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 pt-24 pb-20 md:pt-32 md:pb-28">
          <nav className="flex items-center gap-1.5 text-xs text-zinc-400 mb-8">
            <Link to="/" className="hover:text-zinc-200">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-zinc-200">Road Trip Ready Inspection</span>
          </nav>

          <div className="inline-flex items-center gap-2 rounded-full border border-orange-500/40 bg-orange-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-orange-300 mb-5">
            <Sun className="w-4 h-4" /> Mobile · Northeast Florida
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.05]" style={{ fontFamily: 'Space Grotesk' }} data-testid="rtr-h1">
            Road Trip Ready Inspection
          </h1>
          <p className="mt-5 text-lg md:text-2xl text-zinc-200 font-medium max-w-2xl leading-relaxed">
            Don&rsquo;t let a breakdown become part of your vacation.<br className="hidden sm:block" />
            Know your vehicle is ready before you leave.
          </p>

          <div className="mt-7 inline-flex items-baseline gap-2 rounded-2xl border border-orange-500/40 bg-black/40 backdrop-blur px-6 py-3">
            <span className="text-sm uppercase tracking-widest text-orange-300 font-semibold">Only</span>
            <span className="text-4xl md:text-5xl font-extrabold text-orange-500" style={{ fontFamily: 'Space Grotesk' }}>$99</span>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button onClick={() => navigate('/book?tier=roadtrip')} className="rounded-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 h-auto text-base font-semibold shadow-lg shadow-orange-500/25 transition-transform hover:scale-[1.02]" data-testid="rtr-hero-book-btn">
              Book Your Inspection <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <a href="tel:+13213003256" className="inline-flex items-center justify-center gap-2 rounded-full border border-zinc-500 text-white hover:bg-white/10 px-8 py-3 font-semibold transition-colors" data-testid="rtr-hero-call-btn">
              <Phone className="w-4 h-4" /> Call Now
            </a>
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="max-w-6xl mx-auto px-6 py-20" data-testid="rtr-why-matters">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4" style={{ fontFamily: 'Space Grotesk' }}>Why It Matters</h2>
        <p className="text-zinc-400 text-center max-w-2xl mx-auto mb-12">A few minutes of preparation is the difference between a memorable trip and a memorable breakdown.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {WHY_MATTERS.map((c, i) => (
            <div key={i} className="rounded-2xl border border-zinc-800 bg-[#161618] p-6 hover:border-orange-500/50 transition-colors" data-testid={`rtr-why-${i}`}>
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mb-4">
                <c.Icon className="w-6 h-6 text-orange-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-1.5" style={{ fontFamily: 'Space Grotesk' }}>{c.title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* What's Included */}
      <section className="max-w-6xl mx-auto px-6 py-16" data-testid="rtr-included">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>What&rsquo;s Included</h2>
          <p className="text-zinc-400">A complete pre-trip safety check — everything below is part of your $99 inspection.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {INCLUDED.map((item, i) => (
            <div key={i} className="flex items-center gap-4 rounded-2xl border border-zinc-800 bg-[#161618] p-5 hover:border-orange-500/40 transition-colors" data-testid={`rtr-included-${i}`}>
              <div className="w-11 h-11 shrink-0 rounded-xl bg-orange-500/10 flex items-center justify-center">
                <item.Icon className="w-5 h-5 text-orange-400" />
              </div>
              <span className="font-semibold text-white text-[15px]">{item.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Readiness Score */}
      <section className="max-w-4xl mx-auto px-6 py-20" data-testid="rtr-score">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-3">
            <Trophy className="w-6 h-6 text-orange-500" />
            <span className="text-orange-400 font-semibold tracking-wide uppercase text-sm">Digital Readiness Score</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>Your Road Trip Readiness Score</h2>
          <p className="text-zinc-400 mt-3 max-w-2xl mx-auto">Every inspection ends with a personalized score based on our findings, so you know exactly where your vehicle stands before you travel.</p>
        </div>

        <div className="rounded-3xl border border-zinc-800 bg-[#141416] p-6 md:p-10">
          {/* Segmented scale */}
          <div className="flex rounded-full overflow-hidden h-3 mb-6">
            {SCORE_LEVELS.map((l) => (
              <div key={l.label} className="flex-1" style={{ backgroundColor: l.color }} />
            ))}
          </div>
          <div className="grid gap-3">
            {SCORE_LEVELS.map((l, i) => (
              <div key={l.label} className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-[#1b1b1e] px-4 py-3" data-testid={`rtr-score-level-${i}`}>
                <span className="w-3.5 h-3.5 rounded-full shrink-0" style={{ backgroundColor: l.color }} />
                <span className="font-semibold text-white text-[15px]">{l.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose CarLens */}
      <section className="max-w-6xl mx-auto px-6 py-16" data-testid="rtr-why-carlens">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12" style={{ fontFamily: 'Space Grotesk' }}>Why Choose CarLens</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {WHY_CARLENS.map((c, i) => (
            <div key={i} className="rounded-2xl border border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-950 p-6 hover:border-orange-500/50 transition-colors" data-testid={`rtr-choose-${i}`}>
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mb-4">
                <c.Icon className="w-6 h-6 text-orange-400" />
              </div>
              <h3 className="text-base font-bold text-white mb-1.5" style={{ fontFamily: 'Space Grotesk' }}>{c.title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Perfect For */}
      <section className="max-w-5xl mx-auto px-6 py-16" data-testid="rtr-perfect-for">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-10" style={{ fontFamily: 'Space Grotesk' }}>Perfect For</h2>
        <div className="flex flex-wrap justify-center gap-3">
          {PERFECT_FOR.map((p) => (
            <span key={p} className="inline-flex items-center gap-2 rounded-full border border-zinc-700 bg-[#161618] px-5 py-2.5 text-sm text-zinc-200">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" /> {p}
            </span>
          ))}
        </div>
      </section>

      {/* CTA Banner */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <div className="rounded-3xl bg-gradient-to-br from-orange-500 to-orange-600 p-10 sm:p-14 text-center" data-testid="rtr-cta-banner">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white" style={{ fontFamily: 'Space Grotesk' }}>Ready For Your Next Adventure?</h2>
          <p className="mt-3 text-orange-50 text-lg max-w-2xl mx-auto">Know your vehicle is ready before you hit the road. Book your Road Trip Ready Inspection today.</p>
          <Button onClick={() => navigate('/book?tier=roadtrip')} className="mt-7 rounded-full bg-white text-orange-600 hover:bg-orange-50 px-8 py-6 h-auto text-base font-semibold" data-testid="rtr-cta-banner-btn">
            Schedule My Inspection <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-16" data-testid="rtr-faq">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-3">
            <HelpCircle className="w-6 h-6 text-orange-500" />
            <span className="text-orange-400 font-semibold tracking-wide uppercase text-sm">FAQ</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>Frequently Asked Questions</h2>
        </div>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <details key={i} className="group rounded-2xl border border-zinc-800 bg-[#161618] px-5 py-4 hover:border-orange-500/40 transition-colors" data-testid={`rtr-faq-${i}`}>
              <summary className="flex items-center justify-between cursor-pointer list-none text-white font-semibold text-[15px] md:text-base">
                <span>{f.q}</span>
                <ArrowRight className="w-4 h-4 text-orange-400 flex-shrink-0 ml-4 transition-transform group-open:rotate-90" />
              </summary>
              <p className="mt-3 text-[15px] leading-7 text-zinc-300">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Trust Banner */}
      <section className="max-w-5xl mx-auto px-6 py-16" data-testid="rtr-trust">
        <div className="rounded-3xl border border-zinc-800 bg-[#141416] p-10 text-center">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-2xl md:text-3xl font-extrabold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            <span className="text-white">Inspect.</span>
            <span className="text-white">Inform.</span>
            <span className="text-white">Protect.</span>
          </div>
          <p className="mt-4 text-orange-400 text-xl font-semibold">Travel With Confidence.</p>
        </div>
      </section>

      {/* Internal links */}
      <section className="max-w-5xl mx-auto px-6 pb-28 md:pb-20" data-testid="rtr-internal-links">
        <h2 className="text-2xl font-bold text-white mb-5 text-center" style={{ fontFamily: 'Space Grotesk' }}>Explore More CarLens Services</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            { to: '/services/pre-purchase-inspection', label: 'Signature Pre-Purchase Inspection' },
            { to: '/book', label: 'Vehicle Health Memberships' },
            { to: '/firstlook', label: 'FirstLook Consultation' },
            { to: '/contact', label: 'Contact CarLens' },
            { to: '/', label: 'Back to Home' },
          ].map((l) => (
            <Link key={l.to + l.label} to={l.to} className="group flex items-center justify-between rounded-2xl border border-zinc-800 bg-[#161618] px-5 py-4 hover:border-orange-500/50 transition-colors" data-testid={`rtr-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}>
              <span className="font-semibold text-white group-hover:text-orange-300 transition-colors">{l.label}</span>
              <ArrowRight className="w-4 h-4 text-orange-400" />
            </Link>
          ))}
        </div>
      </section>

      {/* Sticky mobile Book Now bar */}
      <div className="fixed bottom-0 inset-x-0 z-50 sm:hidden border-t border-zinc-800 bg-[#0e0e10]/95 backdrop-blur px-4 py-3" data-testid="rtr-sticky-book">
        <Button onClick={() => navigate('/book?tier=roadtrip')} className="w-full rounded-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-6 h-auto text-base font-semibold shadow-lg shadow-orange-500/25">
          Book Your Inspection · $99 <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

export default RoadTripReady;
